import React, { useMemo } from 'react';
import { BiX } from 'react-icons/bi';

import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { IWidgetToggleIcon } from './types';
import { widgetUtils } from './utils';
import WidgetIconTypeIcon from './WidgetIconTypeIcon';

const WidgetToggleIcon = ({
  icon,
  message,
  theme = 'light',
  color = 'blue',
  hasNotificationBadge = false,
  position = 'right',
  hideCloseButton,
  shape,
  notificationMessageColor,
}: IWidgetToggleIcon) => {
  const correctSrc = useMemo(() => {
    return icon.value && (icon.value as any) instanceof File ? URL.createObjectURL(icon.value as any) : icon.value;
  }, [icon]);

  return (
    <div className={`tw-w-full ${position === 'right' ? 'tw-justify-end' : 'tw-justify-start'} tw-items-center tw-gap-3 tw-inline-flex tw-relative`}>
      {!hideCloseButton && (
        <div className={`tw-absolute ${position === 'left' ? 'tw-left-0.5' : 'tw-right-0.5'} tw-top-0 tw-p-0.5 tw-z-[1]`}>
          <button
            id="btn-widget-x"
            type="button"
            disabled
            className="tw-border-none tw-bg-black tw-rounded-full tw-w-4 tw-h-4 tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center"
          >
            <BiX size={24} className="tw-text-white" />
          </button>
        </div>
      )}

      {position === 'right' && message && (
        <div className="tw-w-full tw-h-10 tw-relative tw-flex tw-items-center tw-justify-end">
          <div
            className={`tw-px-3 tw-py-[7px] ${
              theme === 'dark' ? 'tw-bg-gray-200' : 'tw-bg-gray-800'
            } tw-rounded-2xl tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex tw-text-center tw-relative  tw-text-lg tw-font-bold tw-font-['Sofia Sans'] tw-leading-7 tw-pr-[15px] tw-select-none`}
            style={{
              color: widgetUtils.bgColorMapper[notificationMessageColor] ?? notificationMessageColor,
            }}
          >
            {message}
            <div
              className={`tw-w-4 tw-h-4 ${
                theme === 'dark' ? 'tw-bg-gray-200' : 'tw-bg-gray-800'
              } tw-absolute tw-right-[-4px] tw-top-1/2 tw--translate-y-1/2 tw-rotate-45`}
            />
          </div>
        </div>
      )}
      <button
        id="btn-widget-video"
        className={`tw-w-[60px] tw-h-[60px] tw-shrink-0  tw-border tw-border-solid tw-border-gray-400 tw-p-0.5 tw-relative ${
          icon?.type === 'video' ? (shape === 'circular' ? 'tw-rounded-full' : 'tw-rounded') : 'tw-rounded-full'
        }`}
        type="button"
        disabled
      >
        <div
          className={`tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center ${
            icon?.type === 'video' ? (shape === 'circular' ? 'tw-rounded-full' : 'tw-rounded') : 'tw-rounded-full'
          } `}
          style={{ backgroundColor: widgetUtils.bgColorMapper[color] ?? color }}
        >
          {icon?.type === 'icon' ? (
            <React.Fragment>{WidgetIconTypeIcon({ icon: icon?.value, size: 25 })}</React.Fragment>
          ) : icon?.type === 'image' ? (
            <img src={correctSrc} key={correctSrc} alt="" style={{ objectFit: 'cover' }} className="tw-rounded-[999px] tw-w-full tw-h-full" />
          ) : (
            <video
              muted
              playsInline
              autoPlay
              loop
              style={{ objectFit: 'cover' }}
              key={correctSrc as string}
              className={`${shape === 'circular' ? 'tw-rounded-[999px]' : 'tw-rounded'} tw-h-full tw-w-full`}
            >
              <source src={correctSrc}></source>
            </video>
          )}
        </div>

        {hasNotificationBadge && (
          <div
            className={`tw-w-2 tw-h-2 ${
              position === 'right' ? 'tw-left-0' : 'tw-right-0'
            } tw-bottom-[5px] tw-absolute tw-bg-[red] tw-animate-pulse tw-rounded-[5px] tw-shadow-[0_0_0_2px_rgba(0,0,0,0.2)]`}
          />
        )}
      </button>
      {position === 'left' && message && (
        <div className="tw-w-full tw-h-10 tw-relative">
          <div
            className={`tw-px-3 tw-py-[7px] ${
              theme === 'dark' ? 'tw-bg-gray-200 ' : 'tw-bg-gray-800 '
            } tw-rounded-2xl tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex tw-text-center tw-relative  tw-text-lg tw-font-bold tw-font-['Sofia Sans'] tw-leading-7 tw-pl-[15px]`}
            style={{
              color: widgetUtils.bgColorMapper[notificationMessageColor] ?? notificationMessageColor,
            }}
          >
            {message}
            <div
              className={`tw-w-4 tw-h-4 ${
                theme === 'dark' ? 'tw-bg-gray-200 tw-text-gray-700' : 'tw-bg-gray-800 tw-text-gray-50'
              } tw-absolute tw-left-[-6px] tw-top-1/2 tw--translate-y-1/2 tw-rotate-45`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WrappedErrorBoundary(WidgetToggleIcon);
